// src/hooks/useCrudList.ts

import { useState, useCallback } from 'react';
import { apiRequest } from '../utils/api';
import type { MessageSetter } from '../utils/messages';

interface UseCrudListOptions {
  endpoint: string;           // e.g. '/api/users'
  useAuth?: boolean;
  setMessage?: MessageSetter;
}

/**
 * Generic CRUD list helper for resources with an `id` field.
 */
export function useCrudList<T extends { id: string }>(
  options: UseCrudListOptions
) {
  const { endpoint, useAuth = true, setMessage } = options;

  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest<T[]>(endpoint, {}, { useAuth, setMessage });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [endpoint, useAuth, setMessage]);

  const create = async (body: any, successMessage?: string): Promise<T> => {
    const newItem = await apiRequest<T>(
      endpoint,
      { method: 'POST', body: JSON.stringify(body) },
      { useAuth, setMessage, successMessage }
    );
    setItems(prev => [...prev, newItem]);
    return newItem;
  };

  const update = async (id: string, body: any, successMessage?: string): Promise<T> => {
    const updated = await apiRequest<T>(
      `${endpoint}/${id}`,
      { method: 'PUT', body: JSON.stringify(body) },
      { useAuth, setMessage, successMessage }
    );
    setItems(prev => prev.map(i => (i.id === id ? updated : i)));
    return updated;
  };

  const remove = async (id: string, successMessage?: string): Promise<void> => {
    await apiRequest<void>(
      `${endpoint}/${id}`,
      { method: 'DELETE' },
      { useAuth, setMessage, successMessage }
    );
    setItems(prev => prev.filter(i => i.id !== id));
  };

  return {
    items,
    setItems,
    loading,
    fetchAll,
    create,
    update,
    remove,
  };
}

